import { useInfiniteQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable, type RowSelectionState } from "@tanstack/react-table";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Check, Columns3, ExternalLink, FilterX, MessageCircle, MessagesSquare, PanelRightOpen, RotateCw, ScrollText, SlidersHorizontal, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { IntentChip, SentimentChip } from "@/components/labels";
import { Badge } from "@/components/ui/badge";
import { Button, IconButton } from "@/components/ui/button";
import { Checkbox, Select } from "@/components/ui/controls";
import { Menu, MenuContent, MenuTrigger, Popover, PopoverContent, PopoverTrigger } from "@/components/ui/overlays";
import { DropdownMenu } from "radix-ui";
import { Skeleton } from "@/components/ui/misc";
import { Page } from "@/components/ui/page";
import { PageHeader } from "@/components/kit";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { PostPicker, PostThumb } from "@/features/content/post-quick";
import { api, getCurrentOrgId } from "@/lib/api/client";
import type { ApiError } from "@/lib/api/errors";
import { useApiQuery } from "@/lib/api/hooks";
import { fmtDateTime, fmtRelative } from "@/lib/format";
import { useMe } from "@/hooks/data";
import { isPlatformAdmin } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { usePeek } from "@/lib/peek";
import type { AutomationRun, Page as PageOf, PostRef, Rule } from "@/lib/types";
import { cn } from "@/lib/utils";
import { ACTION_TYPES } from "./model";
import { RunCommentStatus } from "./run-comment-status";
import { RESULT_TONE, retryArgs, runErrorInfo, useRetryRun, useRetryRuns } from "./run-quick";

type Result = AutomationRun["result"];
const RESULTS: Result[] = ["executed", "pending_approval", "suggested", "skipped", "failed"];
export { RESULT_TONE };

const TIMES = { "24h": 24, "7d": 24 * 7, "30d": 24 * 30 } as const;
type Time = keyof typeof TIMES;

/** Filters live in the URL, so a filtered view (e.g. "failed runs of this rule" from a ⚠ peek) is a link. */
export const runsSearchSchema = z.object({
  ruleId: z.string().optional().catch(undefined),
  postId: z.string().optional().catch(undefined),
  status: z.enum(["executed", "pending_approval", "suggested", "skipped", "failed"]).optional().catch(undefined),
  action: z.string().optional().catch(undefined),
  time: z.enum(["24h", "7d", "30d"]).optional().catch(undefined),
});
export type RunsSearch = z.infer<typeof runsSearchSchema>;

const col = createColumnHelper<AutomationRun>();

/** A checkable menu row (the "Columns" menu). */
function ColumnItem({ checked, onCheckedChange, children }: { checked: boolean; onCheckedChange: (v: boolean) => void; children: string }) {
  return (
    <DropdownMenu.CheckboxItem
      checked={checked}
      onCheckedChange={onCheckedChange}
      onSelect={(e) => e.preventDefault()}
      className="relative flex cursor-pointer select-none items-center gap-2.5 rounded-xl px-2.5 py-2 pe-8 text-[13.5px] text-ink outline-none data-[highlighted]:bg-surface-2"
    >
      {children}
      <DropdownMenu.ItemIndicator className="absolute end-2.5 text-accent">
        <Check className="size-4" />
      </DropdownMenu.ItemIndicator>
    </DropdownMenu.CheckboxItem>
  );
}

/** Columns that are off by default (ALP-84: 4 columns; the rest via "Columns"). */
type Optional = "event" | "labels";

/** "Hid the comment + Tagged the contact · comment": what a run did, in words, for the log row. */
export function runSentence(run: Pick<AutomationRun, "action" | "eventRef" | "matched">, t: ReturnType<typeof useT>["t"]): string {
  const [kind] = run.eventRef.split(":");
  const event = kind === "message" ? t("automation.log.messageWord") : t("automation.log.commentWord");
  if (!run.action) return t("automation.log.sentenceNoAction", { event });
  const action = run.action
    .split(",")
    .map((x) => t(`automation.actions.${x as "hide"}`, { defaultValue: x }))
    .join(" + ");
  return t("automation.log.sentence", { action, event });
}

export function RunsPage({ search }: { search: RunsSearch }) {
  const { t, lang } = useT();
  const navigate = useNavigate();
  const { open } = usePeek();
  const staff = isPlatformAdmin(useMe().data); // the AI trace link is for Malfak staff only
  const rules = useApiQuery("rules", {});
  const ruleMap = useMemo(() => new Map<string, Rule>((rules.data ?? []).map((r) => [r.id, r])), [rules.data]);
  const posts = useApiQuery("posts", { query: { limit: 100, status: "published" } }, { staleTime: 60_000 });
  const postRefs: PostRef[] = useMemo(
    () => (posts.data?.items ?? []).filter((p) => p.publications.some((x) => x.status === "published")).map((p) => ({ postId: p.id, publicationId: p.publications[0]?.id ?? null, accountId: p.publications[0]?.accountId ?? null, caption: p.caption, thumbnailUrl: p.media[0]?.url ?? null, permalink: null })),
    [posts.data],
  );
  const [selection, setSelection] = useState<RowSelectionState>({});
  const [extra, setExtra] = useState<Set<Optional>>(() => new Set());
  const moreFilters = (search.postId ? 1 : 0) + (search.action ? 1 : 0);
  const retry = useRetryRun();
  const retryMany = useRetryRuns();

  const set = (patch: Partial<RunsSearch>) => {
    setSelection({});
    void navigate({ to: "/automation/runs", search: { ...search, ...patch }, replace: true });
  };
  // a stable "from" per filter choice (recomputed when the time filter changes, not on every render)
  const from = useMemo(() => (search.time ? new Date(Date.now() - TIMES[search.time] * 3600_000).toISOString() : undefined), [search.time]);
  const query = { ruleId: search.ruleId, postId: search.postId, result: search.status, action: search.action, from };

  const runs = useInfiniteQuery<PageOf<AutomationRun>, ApiError>({
    queryKey: ["runs", getCurrentOrgId(), "infinite", query],
    queryFn: ({ pageParam, signal }) => api("runs", { query: { ...query, cursor: pageParam as string | undefined, limit: 25 }, signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const data = useMemo(() => runs.data?.pages.flatMap((p) => p.items) ?? [], [runs.data]);
  useEffect(() => setSelection((s) => Object.fromEntries(Object.entries(s).filter(([id]) => data.some((r) => r.id === id && r.result === "failed")))), [data]);
  const selectedIds = Object.keys(selection).filter((k) => selection[k]);
  const failed = data.filter((r) => r.result === "failed");
  const filtered = !!(search.ruleId || search.postId || search.status || search.action || search.time);

  const columns = useMemo(
    () => [
      col.display({
        id: "select",
        header: ({ table }) =>
          failed.length > 0 ? (
            <Checkbox
              aria-label={t("quick.run.filters.selectAllFailed")}
              checked={selectedIds.length > 0 && selectedIds.length === failed.length ? true : selectedIds.length ? "indeterminate" : false}
              onCheckedChange={() => table.setRowSelection(selectedIds.length ? {} : Object.fromEntries(failed.map((r) => [r.id, true])))}
            />
          ) : null,
        cell: ({ row }) =>
          row.getCanSelect() ? <Checkbox aria-label={t("quick.run.filters.selectRun")} checked={row.getIsSelected()} onCheckedChange={(v) => row.toggleSelected(v === true)} onClick={(e) => e.stopPropagation()} data-testid="run-select" /> : null,
      }),
      col.accessor("createdAt", {
        header: () => t("automation.runs.when"),
        cell: ({ getValue }) => (
          <time dateTime={getValue()} title={fmtDateTime(getValue())} className="whitespace-nowrap text-ink-3">
            {fmtRelative(getValue())}
          </time>
        ),
      }),
      col.accessor("ruleName", {
        header: () => t("automation.runs.rule"),
        cell: ({ row }) => {
          const r = row.original.ruleId ? ruleMap.get(row.original.ruleId) : undefined;
          return (
            <span className="flex min-w-0 items-center gap-2">
              <span className="grid size-7 shrink-0 place-items-center rounded-control text-[14px]" style={{ background: `color-mix(in srgb, ${r?.color ?? "var(--accent)"} 14%, transparent)` }} aria-hidden>
                {r?.icon ?? "⚡"}
              </span>
              <span className={cn("truncate", r ? "font-medium text-ink" : "text-ink-2")}>{row.original.ruleName ?? r?.name ?? t("automation.runs.deleted")}</span>
            </span>
          );
        },
      }),
      col.accessor("action", {
        id: "what",
        header: () => t("automation.log.whatHappened"),
        cell: ({ row }) => {
          const failure = row.original.result === "failed" ? runErrorInfo(row.original, t) : null;
          const p = row.original.post;
          return (
            <span className="flex min-w-0 items-center gap-2" data-testid="run-sentence">
              {p ? (
                <span onClick={(e) => e.stopPropagation()} className="shrink-0">
                  <PostThumb post={p} size={28} />
                </span>
              ) : null}
              <span className="block min-w-0">
                <span className="block truncate text-ink-2">{runSentence(row.original, t)}</span>
                <span onClick={(e) => e.stopPropagation()} className="contents">
                  <RunCommentStatus eventRef={row.original.eventRef} className="mt-0.5" />
                </span>
                {failure && (
                  <span className="mt-0.5 block max-w-[320px] truncate text-[11.5px] text-bad" title={[failure.message, failure.detail].filter(Boolean).join(": ")} data-testid="run-row-error">
                    {failure.message}
                  </span>
                )}
              </span>
            </span>
          );
        },
      }),
      col.accessor("result", {
        header: () => t("automation.runs.result"),
        cell: ({ row, getValue }) => (
          <span className="inline-flex items-center gap-1.5">
            <Badge tone={RESULT_TONE[getValue()]}>{t(`automation.runs.results.${getValue()}`)}</Badge>
            {row.original.retryOf && <RotateCw className="size-3.5 text-ink-3" aria-label={t("quick.run.retryOf")} />}
          </span>
        ),
      }),
      ...(extra.has("event") ? [
      col.accessor("eventRef", {
        id: "event",
        header: () => t("automation.runs.event"),
        cell: ({ row, getValue }) => {
          const [kind] = getValue().split(":");
          const Icon = kind === "message" ? MessagesSquare : MessageCircle;
          const p = row.original.post;
          return (
            <span className="flex min-w-0 items-center gap-2 text-ink-2" onClick={(e) => e.stopPropagation()}>
              {p ? <PostThumb post={p} size={30} /> : <Icon className="size-4 text-ink-3" />}
              <span className="truncate text-[12.5px]">{kind === "message" ? t("automation.runs.eventMessage") : t("automation.runs.eventComment")}</span>
            </span>
          );
        },
      }),
      ] : []),
      ...(extra.has("labels") ? [
      col.accessor("labels", {
        header: () => t("automation.runs.labels"),
        cell: ({ getValue }) => {
          const l = getValue();
          return l ? (
            <span className="flex flex-wrap items-center gap-1">
              <IntentChip intent={l.intent} confidence={l.confidence} size="xs" />
              <SentimentChip sentiment={l.sentiment} iconOnly />
            </span>
          ) : (
            <span className="text-ink-3">—</span>
          );
        },
      }),
      ] : []),
      col.display({
        id: "actions",
        header: () => <span className="sr-only">{t("automation.runs.details")}</span>,
        cell: ({ row }) => (
          <span className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            {staff && row.original.traceUrl && (
              <a href={row.original.traceUrl} target="_blank" rel="noreferrer noopener" className="grid size-7 place-items-center rounded-control text-ink-3 hover:bg-surface-2 hover:text-ink" aria-label={t("automation.runs.openTrace")} title={t("automation.runs.openTrace")}>
                <ExternalLink className="size-3.5" />
              </a>
            )}
            {row.original.result === "failed" && (
              <IconButton size="icon-xs" variant="ghost" label={t("quick.run.retry")} data-testid="run-retry-row" loading={retry.isPending && retry.variables?.params.runId === row.original.id} onClick={() => retry.mutate(retryArgs(row.original.id))}>
                <RotateCw />
              </IconButton>
            )}
            <IconButton size="icon-xs" variant="ghost" label={t("quick.run.peek")} onClick={() => open("run", row.original.id)}>
              <PanelRightOpen className="flip-rtl" />
            </IconButton>
          </span>
        ),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, lang, ruleMap, failed.length, selectedIds.length, retry.isPending, retry.variables, extra, staff],
  );

  const table = useReactTable({
    data,
    columns,
    state: { rowSelection: selection },
    onRowSelectionChange: setSelection,
    enableRowSelection: (row) => row.original.result === "failed",
    getRowId: (r) => r.id,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Page wide>
      <PageHeader
        eyebrow={
          <Link to="/automation" className="inline-flex items-center gap-1 hover:text-ink">
            <ArrowLeft className="flip-rtl size-3.5" />
            {t("automation.rulesTitle")}
          </Link>
        }
        title={t("automation.runs.title")}
        hint={t("automation.runs.description")}
      />

      <div className="mb-3 flex flex-wrap items-center gap-2" role="group" aria-label={t("quick.run.filters.title")} data-testid="runs-filters">
        <Select
          size="sm"
          className="w-52"
          value={search.ruleId ?? "all"}
          onValueChange={(v) => set({ ruleId: v === "all" ? undefined : v })}
          aria-label={t("quick.run.filters.rule")}
          options={[{ value: "all", label: t("quick.run.filters.anyRule") }, ...(rules.data ?? []).map((r) => ({ value: r.id, label: `${r.icon ?? ""} ${r.name}` }))]}
        />
        <Select
          size="sm"
          className="w-44"
          value={search.status ?? "all"}
          onValueChange={(v) => set({ status: v === "all" ? undefined : (v as Result) })}
          aria-label={t("quick.run.filters.status")}
          options={[{ value: "all" as const, label: t("quick.run.filters.anyStatus") }, ...RESULTS.map((r) => ({ value: r, label: t(`automation.runs.results.${r}`) }))]}
        />
        <Select
          size="sm"
          className="w-40"
          value={search.time ?? "all"}
          onValueChange={(v) => set({ time: v === "all" ? undefined : (v as Time) })}
          aria-label={t("quick.run.filters.time")}
          options={[
            { value: "all", label: t("quick.run.filters.anyTime") },
            { value: "24h", label: t("quick.run.filters.last24h") },
            { value: "7d", label: t("quick.run.filters.last7d") },
            { value: "30d", label: t("quick.run.filters.last30d") },
          ]}
        />
        <Popover>
          <PopoverTrigger asChild>
            <Button size="sm" variant="outline" icon={<SlidersHorizontal />} data-testid="runs-more-filters">
              {t("automation.log.filters")}
              {moreFilters > 0 && <span className="num rounded-md bg-accent-soft px-1.5 text-[11px] text-accent-ink">{moreFilters}</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-72 space-y-3">
            <div className="space-y-1">
              <div className="text-[12px] font-medium text-ink-3">{t("quick.run.filters.post")}</div>
        <PostPicker posts={postRefs} value={search.postId} onChange={(postId) => set({ postId })} anyLabel={t("quick.run.filters.anyPost")} label={t("quick.run.filters.post")} testId="runs-post-filter" />
            </div>
            <div className="space-y-1">
              <div className="text-[12px] font-medium text-ink-3">{t("quick.run.filters.action")}</div>
        <Select
          size="sm"
          className="w-full"
          value={search.action ?? "all"}
          onValueChange={(v) => set({ action: v === "all" ? undefined : v })}
          aria-label={t("quick.run.filters.action")}
          options={[{ value: "all", label: t("quick.run.filters.anyAction") }, ...ACTION_TYPES.map((a) => ({ value: a, label: t(`automation.actions.${a}`) }))]}
        />
            </div>
          </PopoverContent>
        </Popover>
        <Menu>
          <MenuTrigger asChild>
            <Button size="sm" variant="ghost" icon={<Columns3 />} className="ms-auto" data-testid="runs-columns">
              {t("automation.log.columns")}
            </Button>
          </MenuTrigger>
          <MenuContent>
            {(["event", "labels"] as const).map((k) => (
              <ColumnItem
                key={k}
                checked={extra.has(k)}
                onCheckedChange={(on) =>
                  setExtra((cur) => {
                    const next = new Set(cur);
                    if (on) next.add(k);
                    else next.delete(k);
                    return next;
                  })
                }
              >
                {k === "event" ? t("automation.runs.event") : t("automation.runs.labels")}
              </ColumnItem>
            ))}
          </MenuContent>
        </Menu>
        {filtered && (
          <Button size="sm" variant="ghost" icon={<FilterX />} onClick={() => set({ ruleId: undefined, postId: undefined, status: undefined, action: undefined, time: undefined })}>
            {t("quick.run.filters.clear")}
          </Button>
        )}
      </div>

      <AnimatePresence>
        {selectedIds.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            data-testid="runs-bulk-bar"
            className="mb-3 flex items-center gap-2 rounded-field border border-accent/30 bg-accent-soft px-3 py-2 text-[13px] text-accent-ink"
          >
            <span className="font-medium">{t("quick.run.filters.selected", { count: selectedIds.length })}</span>
            <Button
              size="sm"
              variant="primary"
              className="ms-auto"
              icon={<RotateCw />}
              loading={retryMany.isPending}
              data-testid="runs-bulk-retry"
              onClick={() => retryMany.mutate({ body: { runIds: selectedIds }, headers: { "Idempotency-Key": `runs-retry.${selectedIds.length}.${Date.now()}` } }, { onSuccess: () => setSelection({}) })}
            >
              {t("quick.run.retryBulk", { count: selectedIds.length })}
            </Button>
            <IconButton size="icon-sm" variant="ghost" label={t("common.actions.cancel")} onClick={() => setSelection({})}>
              <X />
            </IconButton>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative overflow-hidden rounded-card border border-line bg-surface shadow-xs">
        {runs.isPending ? (
          <div className="space-y-2 p-4" aria-busy="true">
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} className="h-10 rounded-control" />
            ))}
          </div>
        ) : runs.isError ? (
          <ErrorState error={runs.error} onRetry={() => void runs.refetch()} />
        ) : data.length === 0 ? (
          <EmptyState
            icon={ScrollText}
            title={filtered ? t("quick.approvals.noMatch") : t("automation.runs.emptyTitle")}
            hint={filtered ? undefined : t("automation.runs.emptyHint")}
            action={
              filtered ? (
                <Button size="sm" variant="outline" icon={<FilterX />} onClick={() => set({ ruleId: undefined, postId: undefined, status: undefined, action: undefined, time: undefined })}>
                  {t("quick.run.filters.clear")}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="relative overflow-x-auto">
            <table data-testid="runs-table" className="w-full min-w-[720px] border-collapse text-[13px]">
              <thead>
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id} className="border-b border-line bg-surface-2/60">
                    {hg.headers.map((h) => (
                      <th key={h.id} scope="col" className="px-3 py-2.5 text-start text-[11.5px] font-semibold uppercase tracking-wide text-ink-3 first:w-10">
                        {flexRender(h.column.columnDef.header, h.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    data-testid="run-row"
                    data-id={row.id}
                    data-result={row.original.result}
                    onClick={() => open("run", row.original.id)}
                    className={cn("cursor-pointer border-b border-line transition-colors last:border-0 hover:bg-surface-2/50", row.getIsSelected() && "bg-accent-soft/40", !row.original.matched && "text-ink-3")}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-3 py-2 align-middle">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {runs.hasNextPage && (
        <div className="mt-4 flex justify-center">
          <Button variant="outline" loading={runs.isFetchingNextPage} onClick={() => void runs.fetchNextPage()}>
            {t("automation.runs.loadMore")}
          </Button>
        </div>
      )}
    </Page>
  );
}
